/**
 * Event bus implementation using Babylon.js Observable pattern
 */

import { Observable } from "@babylonjs/core";
import type { EventHandler, IDisposable, Unsubscribe } from "./types";

/**
 * Central event bus for manager communication
 * Uses Babylon.js Observable for consistency with the engine
 */
export class EventBus implements IDisposable {
  private observables = new Map<string, Observable<unknown>>();
  private _isDisposed = false;

  /**
   * Check if the event bus has been disposed
   */
  get isDisposed(): boolean {
    return this._isDisposed;
  }

  /**
   * Emit an event with data
   * @param event The event name
   * @param data The data to send with the event
   */
  emit<T = unknown>(event: string, data: T): void {
    if (this._isDisposed) {
      // Silently ignore - caller should check isDisposed if they care
      return;
    }

    const observable = this.getObservable(event);
    observable.notifyObservers(data);
  }

  /**
   * Subscribe to an event
   * @param event The event name
   * @param handler The handler function
   * @returns Function to unsubscribe
   */
  on<T = unknown>(event: string, handler: EventHandler<T>): Unsubscribe {
    if (this._isDisposed) {
      // Return no-op unsubscribe - matches BaseManager pattern
      return () => {};
    }

    const observable = this.getObservable(event);
    // Wrap handler to only pass data, not EventState
    const wrappedHandler = (data: unknown) => {
      handler(data as T);
    };
    const observer = observable.add(wrappedHandler);

    return () => {
      if (!this._isDisposed) {
        observer.remove();
      }
    };
  }

  /**
   * Subscribe to an event once
   * @param event The event name
   * @param handler The handler function
   * @returns Function to unsubscribe
   */
  once<T = unknown>(event: string, handler: EventHandler<T>): Unsubscribe {
    if (this._isDisposed) {
      // Return no-op unsubscribe - matches BaseManager pattern
      return () => {};
    }

    const observable = this.getObservable(event);
    // Wrap handler to only pass data, not EventState
    const wrappedHandler = (data: unknown) => {
      handler(data as T);
    };
    const observer = observable.addOnce(wrappedHandler);

    return () => {
      if (!this._isDisposed) {
        observer.remove();
      }
    };
  }

  /**
   * Check if an event has any observers
   * @param event The event name
   * @returns True if the event has observers
   */
  hasObservers(event: string): boolean {
    const observable = this.observables.get(event);
    return observable ? observable.hasObservers() : false;
  }

  /**
   * Clear all observers for a specific event
   * @param event The event name
   */
  clear(event: string): void {
    const observable = this.observables.get(event);
    if (observable) {
      observable.clear();
    }
  }

  /**
   * Clear all observers for all events
   */
  clearAll(): void {
    this.observables.forEach((observable) => {
      observable.clear();
    });
  }

  /**
   * Dispose of the event bus and clean up all resources
   */
  dispose(): void {
    if (this._isDisposed) return;

    this.clearAll();
    this.observables.clear();
    this._isDisposed = true;
  }

  /**
   * Get or create an observable for an event
   * @param event The event name
   * @returns The observable for the event
   */
  private getObservable(event: string): Observable<unknown> {
    let observable = this.observables.get(event);
    if (!observable) {
      observable = new Observable<unknown>();
      this.observables.set(event, observable);
    }
    return observable;
  }

  /**
   * Get the number of registered events
   */
  get eventCount(): number {
    return this.observables.size;
  }

  /**
   * Get the total number of observers across all events
   */
  get totalObservers(): number {
    let total = 0;
    this.observables.forEach((observable) => {
      // Observable doesn't expose observer count directly, so we track if it has any
      if (observable.hasObservers()) {
        total++;
      }
    });
    return total;
  }
}
